(() => {
  if (customElements.get("announcement-bar")) return;

  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.cleanup = [];
      this.items = [];
      this.itemsWrapper = null;
      this.prevBtn = null;
      this.nextBtn = null;
      this.index = 0;
      this.autoplay = false;
      this.intervalMs = 5000;
      this.timerId = null;
      this.isDraggable = false;
      this.isDragging = false;
      this.startX = 0;
      this.deltaX = 0;
      this.didDrag = false;
      this.pointerId = null;
      this.hasPointerCapture = false;

      this.handlePrevClick = () => this.showPrev();
      this.handleNextClick = () => this.showNext();
      this.handleMouseEnter = () => this.stopAutoplay();
      this.handleMouseLeave = () => this.startAutoplay();
      this.handlePointerDown = (event) => this.onPointerDown(event);
      this.handlePointerMove = (event) => this.onPointerMove(event);
      this.handlePointerUp = (event) => this.onPointerUp(event);
      this.handleClick = (event) => this.onClick(event);
      this.handleResize = () => this.syncItemWidth();
    }

    connectedCallback() {
      this.items = Array.from(
        this.querySelectorAll("[data-announcement-item]"),
      );
      this.itemsWrapper = this.querySelector("[data-announcement-items]");
      this.prevBtn = this.querySelector("[data-announcement-prev]");
      this.nextBtn = this.querySelector("[data-announcement-next]");
      this.autoplay = this.dataset.autoplay === "true";
      this.isDraggable = this.hasAttribute("data-draggable");
      this.intervalMs = (parseInt(this.dataset.interval, 10) || 5) * 1000;

      if (!this.items.length) return;

      this.syncItemWidth();
      this.showAt(0);

      this.addListener(this.prevBtn, "click", this.handlePrevClick);
      this.addListener(this.nextBtn, "click", this.handleNextClick);
      this.addListener(window, "resize", this.handleResize);

      if (this.isDraggable && this.itemsWrapper && this.items.length > 1) {
        this.addListener(
          this.itemsWrapper,
          "pointerdown",
          this.handlePointerDown,
        );
        this.addListener(
          this.itemsWrapper,
          "pointermove",
          this.handlePointerMove,
        );
        this.addListener(this.itemsWrapper, "pointerup", this.handlePointerUp);
        this.addListener(
          this.itemsWrapper,
          "pointercancel",
          this.handlePointerUp,
        );
        this.addListener(this.itemsWrapper, "click", this.handleClick, true);
      }

      if (this.autoplay && this.items.length > 1) {
        this.startAutoplay();
        this.addListener(this, "mouseenter", this.handleMouseEnter);
        this.addListener(this, "mouseleave", this.handleMouseLeave);
      }

      if (Shopify.designMode) {
        this.addListener(document, "shopify:block:select", (e) => {
          if (!this.contains(e.target)) return;
          const idx = this.items.indexOf(e.target);
          if (idx !== -1) {
            this.stopAutoplay();
            this.showAt(idx);
          }
        });
        this.addListener(document, "shopify:block:deselect", (e) => {
          if (!this.contains(e.target)) return;
          if (this.autoplay && this.items.length > 1) {
            this.startAutoplay();
          }
        });
      }
    }

    disconnectedCallback() {
      this.cleanup.forEach((remove) => remove());
      this.cleanup = [];
      this.stopAutoplay();
      this.isDragging = false;
      this.pointerId = null;
    }

    addListener(el, event, handler, options) {
      if (!el) return;
      el.addEventListener(event, handler, options);
      this.cleanup.push(() => el.removeEventListener(event, handler, options));
    }

    onPointerDown(event) {
      if (!event.isPrimary || event.button !== 0) return;
      this.isDragging = true;
      this.didDrag = false;
      this.deltaX = 0;
      this.startX = event.clientX;
      this.pointerId = event.pointerId;
      this.hasPointerCapture = false;
      if (this.itemsWrapper) {
        this.itemsWrapper.classList.add("cursor-grabbing");
      }
    }

    onPointerMove(event) {
      if (!this.isDragging || event.pointerId !== this.pointerId) return;
      this.deltaX = event.clientX - this.startX;
      if (!this.hasPointerCapture && Math.abs(this.deltaX) > 6) {
        this.hasPointerCapture = true;
        if (this.itemsWrapper) {
          this.itemsWrapper.setPointerCapture(this.pointerId);
        }
      }
      if (Math.abs(this.deltaX) > 6) {
        this.didDrag = true;
      }
    }

    onPointerUp(event) {
      if (!this.isDragging || event.pointerId !== this.pointerId) return;
      if (this.itemsWrapper) {
        if (this.hasPointerCapture) {
          this.itemsWrapper.releasePointerCapture(this.pointerId);
        }
        this.itemsWrapper.classList.remove("cursor-grabbing");
      }
      this.isDragging = false;
      this.pointerId = null;
      this.hasPointerCapture = false;

      const absDelta = Math.abs(this.deltaX);
      if (absDelta > 30) {
        if (this.deltaX < 0) {
          this.showNext();
        } else {
          this.showPrev();
        }
      }

      this.deltaX = 0;
    }

    onClick(event) {
      if (this.didDrag) {
        event.preventDefault();
        event.stopPropagation();
        this.didDrag = false;
      }
    }

    syncItemWidth() {
      // Slides are grid-stacked (all in one cell), so the container keeps its
      // natural width from the widest slide — no manual measuring needed.
    }

    getMaxAllowedWidth() {
      if (!this.itemsWrapper) return 0;
      if (
        window.matchMedia &&
        window.matchMedia("(min-width: 749px)").matches
      ) {
        return 0;
      }
      const computed = window.getComputedStyle(this.itemsWrapper);
      const maxWidth = computed.maxWidth;
      let maxFromCss = 0;
      if (maxWidth && maxWidth !== "none") {
        maxFromCss = parseFloat(maxWidth) || 0;
      }

      const parent = this.itemsWrapper.parentElement;
      const parentWidth = parent ? parent.clientWidth : 0;
      if (maxFromCss && parentWidth) {
        return Math.min(maxFromCss, parentWidth);
      }
      return maxFromCss || parentWidth || 0;
    }

    showAt(nextIndex, dir) {
      if (dir === undefined) dir = 1;
      const prev = this.index;
      const first = !this._inited;
      this.items.forEach((item, idx) => {
        if (idx === nextIndex) {
          if (first) {
            item.style.transition = "none";
            item.style.transform = "translateX(0)";
            item.style.opacity = "1";
          } else {
            // place on the incoming side, then slide to centre
            item.style.transition = "none";
            item.style.transform = `translateX(${dir > 0 ? 100 : -100}%)`;
            item.style.opacity = "0";
            void item.offsetWidth; // force reflow so the next transition animates
            item.style.transition = "transform 0.6s ease, opacity 0.6s ease";
            item.style.transform = "translateX(0)";
            item.style.opacity = "1";
          }
          item.style.zIndex = "2";
          item.classList.add("is-active");
        } else if (idx === prev && !first) {
          // slide the outgoing slide off the opposite side
          item.style.transition = "transform 0.6s ease, opacity 0.6s ease";
          item.style.transform = `translateX(${dir > 0 ? -100 : 100}%)`;
          item.style.opacity = "0";
          item.style.zIndex = "1";
          item.classList.remove("is-active");
        } else {
          item.style.transition = "none";
          item.style.transform = `translateX(${dir > 0 ? 100 : -100}%)`;
          item.style.opacity = "0";
          item.style.zIndex = "0";
          item.classList.remove("is-active");
        }
      });
      this.index = nextIndex;
      this._inited = true;
    }

    showNext() {
      this.showAt((this.index + 1) % this.items.length, 1);
    }

    showPrev() {
      this.showAt(
        (this.index - 1 + this.items.length) % this.items.length,
        -1,
      );
    }

    startAutoplay() {
      this.stopAutoplay();
      this.timerId = setInterval(() => {
        this.showNext();
      }, this.intervalMs);
    }

    stopAutoplay() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    }
  }

  customElements.define("announcement-bar", AnnouncementBar);
})();
